/**
 * "Are you sure" for taking portal access away, and a straight answer to which
 * doors are about to close.
 *
 * A confirmation because the action grew. It used to clear one column and could
 * be undone by pressing the neighbouring button; it now also destroys any
 * unredeemed invitation, and an invitation cannot be un-destroyed - the token
 * hash is the only copy of the secret, so a fresh invite means a fresh link and
 * a fresh mail to the client.
 *
 * The wording is picked from what this contact actually holds rather than
 * written once for both. "The link in the mail stops working" is the sentence
 * that matters to somebody undoing a misaddressed invitation, and it is
 * meaningless to somebody revoking a signed-in accountant. A dialog that says
 * both every time is a dialog that gets clicked through.
 *
 * What it does NOT do, and says so: the person's user account survives. We are
 * removing their link to THIS company, not deleting anybody.
 */
import { useState } from "react";
import { UserX } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { Btn, Modal } from "../../ui";

export interface PortalRevokeTarget {
  id: string;
  name: string;
  email: string | null;
  /** Holds a linked portal account. */
  linked: boolean;
  /** Holds an invitation that is still redeemable. */
  invited: boolean;
}

export function ContactPortalRevokeModal({
  target, busy, onConfirm, onClose,
}: {
  target: PortalRevokeTarget;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const [pressed, setPressed] = useState(false);
  const title = t("clients.portalRevoke.title");

  // Both is possible and is its own sentence: an operator can link an account
  // and still have an older invitation outstanding on the same contact.
  const bodyKey =
    target.linked && target.invited ? "clients.portalRevoke.bodyBoth"
    : target.invited ? "clients.portalRevoke.bodyInvite"
    : "clients.portalRevoke.bodyLink";

  return (
    <Modal
      title={title}
      label={title}
      onClose={onClose}
      footer={
        <>
          <Btn onClick={onClose}>{t("common.cancel")}</Btn>
          <Btn
            tone="danger"
            disabled={busy || pressed}
            onClick={() => {
              setPressed(true);
              onConfirm();
            }}
          >
            <UserX className="size-4" />
            {t("clients.portalRevoke.confirm")}
          </Btn>
        </>
      }
    >
      <p className="crm-mut text-[13.25px] leading-relaxed">
        {t(bodyKey, { name: target.name, email: target.email ?? "" })}
      </p>
      <p className="crm-mut mt-3 text-[12.5px] leading-relaxed opacity-80">
        {t("clients.portalRevoke.accountKept")}
      </p>
    </Modal>
  );
}
